import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import { Box, Button, Callout, Card, Column, Typography, useOverlayContext } from 'seagull';
import { Edit } from 'seagull/icons/content-edit';
import { Send } from 'seagull/icons/emails-messages';
import { Eye } from 'seagull/icons/essentials';
import { Lock } from 'seagull/icons/security';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import { BrandHeader } from './components/BrandHeader';
import { OAuthBusinessSelector } from './components/OAuthBusinessSelector';
import { PermissionRow } from './components/PermissionRow';
import styles from './OAuthAuthorizePage.module.scss';
const CLAUDE_BRAND_LABEL = 'Claude';
const AUTHORIZE_SESSION_PARAM = 'authorize_session';
const CLIENT_ID_PARAM = 'client_id';
const PKCE_CHALLENGE_PARAM = 'code_challenge';
const SCOPE_PARAM = 'scope';
export const GetOAuthAuthorizePageTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export function OAuthAuthorizePage() {
    var _queryParams_get, _queryParams_get1, _queryParams_get2, _queryParams_get3;
    const { t: authorizeTexts } = useLocaleKeys('oauth.authorize');
    const testIds = useMemo(()=>GetOAuthAuthorizePageTID(''), []);
    const { OAuthAuthorizePageTestIds } = testIds;
    const location = useLocation();
    const navigate = useNavigate();
    const { api } = useStoreActions();
    const overlayContext = useOverlayContext();
    const { browser: { changeLocation } } = useIoContext();
    const hasUserInfo = useStore(selectors.user.hasUserInfo);
    const workspaces = useStore(selectors.user.workspaces);
    const currentBusinessId = useStore(selectors.user.businessId);
    const impersonatingAs = useStore(selectors.impersonatingAs);
    const [inlineError, setInlineError] = useState(null);
    const [isAuthorizing, setIsAuthorizing] = useState(false);
    const queryParams = useMemo(()=>new URLSearchParams(location.search), [
        location.search
    ]);
    const authorizeSession = (_queryParams_get = queryParams.get(AUTHORIZE_SESSION_PARAM)) !== null && _queryParams_get !== void 0 ? _queryParams_get : '';
    const clientId = (_queryParams_get1 = queryParams.get(CLIENT_ID_PARAM)) !== null && _queryParams_get1 !== void 0 ? _queryParams_get1 : '';
    const pkceChallenge = (_queryParams_get2 = queryParams.get(PKCE_CHALLENGE_PARAM)) !== null && _queryParams_get2 !== void 0 ? _queryParams_get2 : '';
    const scope = (_queryParams_get3 = queryParams.get(SCOPE_PARAM)) !== null && _queryParams_get3 !== void 0 ? _queryParams_get3 : '';
    const businessOptions = useMemo(()=>(workspaces !== null && workspaces !== void 0 ? workspaces : []).map((workspace)=>({
                id: workspace.id(),
                name: workspace.name()
            })), [
        workspaces
    ]);
    const [selectedBusinessId, setSelectedBusinessId] = useState(currentBusinessId !== null && currentBusinessId !== void 0 ? currentBusinessId : '');
    if (hasUserInfo && (authorizeSession == null || authorizeSession === '')) {
        return /*#__PURE__*/ _jsx(Box, {
            className: styles.page,
            children: /*#__PURE__*/ _jsx(Card, {
                bi: {
                    name: 'oauth_authorize_missing_session_card'
                },
                className: styles.card,
                testId: OAuthAuthorizePageTestIds.Root(),
                children: /*#__PURE__*/ _jsx(Callout, {
                    intent: "error",
                    testId: OAuthAuthorizePageTestIds.MissingSessionError(),
                    children: authorizeTexts.errors.missingSession()
                })
            })
        });
    }
    if (!hasUserInfo) {
        const nextParam = encodeURIComponent("".concat(routes.oauthAuthorize).concat(location.search));
        return /*#__PURE__*/ _jsx(Navigate, {
            to: "".concat(routes.login, "?next=").concat(nextParam),
            replace: true
        });
    }
    const handleAllow = async ()=>{
        if (selectedBusinessId == null || selectedBusinessId === '') {
            setInlineError(authorizeTexts.errors.noBusinessSelected());
            return;
        }
        setInlineError(null);
        setIsAuthorizing(true);
        try {
            const response = await api.oauth.issueCode({
                authorizeSession,
                businessId: selectedBusinessId,
                clientId,
                pkceChallenge,
                scope
            });
            if (response.workerRedirect == null || response.workerRedirect === '') {
                overlayContext.showErrorToast(authorizeTexts.errors.missingWorkerRedirect(), 'oauth-authorize-failure');
                setIsAuthorizing(false);
                return;
            }
            const workerUrl = new URL(response.workerRedirect);
            workerUrl.searchParams.set('authorize_session', authorizeSession);
            workerUrl.searchParams.set('code', response.authCode);
            changeLocation(workerUrl.toString());
        } catch (unused) {
            overlayContext.showErrorToast(authorizeTexts.errors.genericFailure(), 'oauth-authorize-failure');
            setIsAuthorizing(false);
        }
    };
    const handleDeny = ()=>{
        overlayContext.showInfoToast(authorizeTexts.canceledToast(), 'oauth-authorize-canceled');
        navigate(routes.home, {
            replace: true
        });
    };
    return /*#__PURE__*/ _jsx(Box, {
        className: styles.page,
        children: /*#__PURE__*/ _jsxs(Card, {
            bi: {
                name: 'oauth_authorize_card'
            },
            className: styles.card,
            testId: OAuthAuthorizePageTestIds.Root(),
            children: [
                /*#__PURE__*/ _jsx(BrandHeader, {
                    claudeLabel: CLAUDE_BRAND_LABEL
                }),
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 2,
                    className: styles.heading,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h4",
                            testId: OAuthAuthorizePageTestIds.Heading(),
                            children: authorizeTexts.heading()
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            skin: "secondary",
                            testId: OAuthAuthorizePageTestIds.Subheading(),
                            children: authorizeTexts.subheading()
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 2,
                    className: styles.section,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            weight: "bold",
                            children: authorizeTexts.signingInAs()
                        }),
                        /*#__PURE__*/ _jsx(OAuthBusinessSelector, {
                            options: businessOptions,
                            selectedBusinessId: selectedBusinessId,
                            onChange: setSelectedBusinessId,
                            testId: OAuthAuthorizePageTestIds.BusinessSelector()
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 4,
                    className: styles.section,
                    testId: OAuthAuthorizePageTestIds.PermissionsList(),
                    children: [
                        /*#__PURE__*/ _jsxs(Column, {
                            gap: 3,
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph1",
                                    weight: "bold",
                                    children: authorizeTexts.permissionsCanTitle()
                                }),
                                /*#__PURE__*/ _jsxs(Column, {
                                    gap: 3,
                                    children: [
                                        /*#__PURE__*/ _jsx(PermissionRow, {
                                            icon: /*#__PURE__*/ _jsx(Eye, {
                                                size: 18
                                            }),
                                            title: authorizeTexts.permissions.viewDataTitle(),
                                            description: authorizeTexts.permissions.viewDataDescription(),
                                            intent: "granted"
                                        }),
                                        /*#__PURE__*/ _jsx(PermissionRow, {
                                            icon: /*#__PURE__*/ _jsx(Edit, {
                                                size: 18
                                            }),
                                            title: authorizeTexts.permissions.createUpdateTitle(),
                                            description: authorizeTexts.permissions.createUpdateDescription(),
                                            intent: "editing"
                                        }),
                                        /*#__PURE__*/ _jsx(PermissionRow, {
                                            icon: /*#__PURE__*/ _jsx(Send, {
                                                size: 18
                                            }),
                                            title: authorizeTexts.permissions.takeActionTitle(),
                                            description: authorizeTexts.permissions.takeActionDescription(),
                                            intent: "caution"
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(Column, {
                            gap: 3,
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph1",
                                    weight: "bold",
                                    children: authorizeTexts.permissionsCannotTitle()
                                }),
                                /*#__PURE__*/ _jsx(PermissionRow, {
                                    icon: /*#__PURE__*/ _jsx(Lock, {
                                        size: 18
                                    }),
                                    title: authorizeTexts.permissions.lockedToBusinessTitle(),
                                    description: authorizeTexts.permissions.lockedToBusinessDescription(),
                                    intent: "denied"
                                })
                            ]
                        })
                    ]
                }),
                inlineError != null ? /*#__PURE__*/ _jsx(Callout, {
                    intent: "error",
                    testId: OAuthAuthorizePageTestIds.InlineError(),
                    className: styles.errorText,
                    children: inlineError
                }) : null,
                impersonatingAs != null ? /*#__PURE__*/ _jsx(Callout, {
                    intent: "warning",
                    testId: OAuthAuthorizePageTestIds.ImpersonationWarning(),
                    children: authorizeTexts.impersonationWarning({
                        email: impersonatingAs
                    })
                }) : null,
                /*#__PURE__*/ _jsxs(Box, {
                    className: styles.actions,
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'deny_oauth_authorization'
                            },
                            skin: "primaryOutline",
                            onClick: handleDeny,
                            testId: OAuthAuthorizePageTestIds.DenyButton(),
                            children: authorizeTexts.deny()
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'allow_oauth_authorization'
                            },
                            onClick: handleAllow,
                            loading: isAuthorizing,
                            testId: OAuthAuthorizePageTestIds.AllowButton(),
                            children: authorizeTexts.allow()
                        })
                    ]
                })
            ]
        })
    });
}
